// Firmware — mobile. Super Admin only.
//
// Same list as the desktop page and the same `useFirmwareFleet` behind it; only
// the presentation differs. A ten-column table is unreadable on a phone, so each
// device is a card with the two dates stacked — but the columns, the filters,
// the selection rules and the send are the same code, so the two shells cannot
// disagree about which devices need updating.

import React, { useState } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, MenuItem, Chip, Checkbox,
  LinearProgress, Alert, FormControlLabel, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import StopIcon from '@mui/icons-material/Stop';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MemoryIcon from '@mui/icons-material/Memory';
import { CenterTitleBar } from '../components/MobileAppBar';
import UploadBinFileDialog from '../../shared/UploadBinFileDialog';
import DeviceFirmwareDialog from '../../shared/DeviceFirmwareDialog';
import { useApp } from '../../../core/state/AppContext';
import { useFirmwareFleet, STATUS_FILTERS } from '../../../core/hooks/useFirmwareFleet';
import { FLEET_LIVE, CURRENT, STALE } from '../../../core/devices/firmwareFleet';
import { FIT_XS } from '../../shared/fitDialog';

const STATE_STYLE = {
  [STALE]: { label: 'Needs update', colour: '#FFB74D' },
  [CURRENT]: { label: 'Up to date', colour: '#4CAF50' },
  unknown: { label: 'Not known', colour: '#9E9E9E' },
};

const when = (ms) => (ms == null ? '—'
  : new Date(ms).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }));

const ago = (ms) => {
  if (ms == null) return 'never';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export default function FirmwareScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const { userId, isSuperAdmin } = useApp();
  const f = useFirmwareFleet(userId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  if (!isSuperAdmin) {
    return (
      <Box sx={{ height: '100%', bgcolor: c.back }}>
        <CenterTitleBar title="Firmware" showBack />
        <Typography sx={{ color: c.text, p: 3, textAlign: 'center' }}>
          This screen is for administrators.
        </Typography>
      </Box>
    );
  }

  const loading = f.phase === 'devices' || f.phase === 'firmware';
  const pct = f.progress.total ? (f.progress.done / f.progress.total) * 100 : 0;

  const selectSx = {
    '& .MuiInputBase-input': { color: c.text, fontSize: 13 },
    '& .MuiSvgIcon-root': { color: c.text },
  };

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <CenterTitleBar
        title="Firmware"
        showBack
        actions={(
          <>
            {/* Publishing needs no fleet load, so it is offered even while the
                list is still reading. */}
            <IconButton
              onClick={() => setUploadOpen(true)}
              aria-label="Publish a firmware build"
              sx={{ color: c.text }}
            >
              <UploadFileIcon />
            </IconButton>
            <IconButton
              onClick={() => setMapOpen(true)}
              aria-label="Set a device's firmware file"
              sx={{ color: c.text }}
            >
              <MemoryIcon />
            </IconButton>
            <IconButton
              onClick={loading ? f.stop : f.load}
              sx={{ color: c.text }}
            >
              {loading ? <StopIcon /> : <RefreshIcon />}
            </IconButton>
          </>
        )}
      />

      {loading && (
        <Box sx={{ px: 1.5, py: 1 }}>
          <LinearProgress variant={f.progress.total ? 'determinate' : 'indeterminate'} value={pct} />
          <Typography sx={{ color: `${c.text}99`, fontSize: 11, mt: 0.5 }}>
            {f.phase === 'devices'
              ? `Reading locations — ${f.progress.done}/${f.progress.total}`
              : `Reading firmware — ${f.progress.done}/${f.progress.total}`}
          </Typography>
        </Box>
      )}

      {f.error && <Alert severity="error" sx={{ m: 1.5 }}>{f.error}</Alert>}

      {f.phase === 'idle' && !f.rows.length && (
        <Box sx={{ textAlign: 'center', px: 3, py: 6 }}>
          <SystemUpdateAltIcon sx={{ fontSize: 48, color: `${c.text}66` }} />
          <Typography sx={{ color: c.text, fontSize: 17, fontWeight: 700, mt: 2 }}>
            Every device, and what it is running
          </Typography>
          {/* The cost is stated before it is paid — a button that silently
              starts a minute of work is a button people press twice. */}
          <Typography sx={{ color: `${c.text}99`, mt: 1, fontSize: 13 }}>
            Reads every location, then every device&apos;s firmware — around 1,400
            requests, so it takes a minute or so. Nothing is sent until you tick
            rows and press Update.
          </Typography>
          <Button
            variant="contained" onClick={f.load}
            sx={{ mt: 3, bgcolor: c.button, color: c.text, textTransform: 'none' }}
          >
            Load all devices
          </Button>
          <Box>
            <Button
              size="small" onClick={() => setUploadOpen(true)}
              startIcon={<UploadFileIcon />}
              sx={{ mt: 1, color: `${c.text}CC`, textTransform: 'none' }}
            >
              Or publish a new build
            </Button>
          </Box>
        </Box>
      )}

      {(f.phase === 'sending' || f.phase === 'done') && (
        <Alert severity={f.phase === 'done' ? 'success' : 'info'} sx={{ m: 1.5 }}>
          <Typography variant="body2">
            {f.phase === 'done'
              ? `Sent to ${f.sent} device${f.sent === 1 ? '' : 's'}. They reflash on their own and drop offline briefly — reload in a minute or two to check.`
              : `Sending… ${f.sent} of ${f.targets.length}`}
          </Typography>
          {f.phase === 'done' && (
            <Button size="small" onClick={f.backToList} sx={{ mt: 0.5 }}>Back to the list</Button>
          )}
        </Alert>
      )}

      {f.rows.length > 0 && (
        <>
          <Box sx={{ px: 1.5, pt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip size="small" label={`${f.counts.total} total`} sx={{ height: 20, fontSize: 11, bgcolor: `${c.text}22`, color: c.text }} />
            <Chip size="small" label={`live ${f.counts.live}`} sx={{ height: 20, fontSize: 11, bgcolor: '#4CAF5033', color: '#A5D6A7' }} />
            <Chip size="small" label={`needs ${f.counts.needsUpdate}`} sx={{ height: 20, fontSize: 11, bgcolor: '#FFB74D33', color: '#FFCC80' }} />
            <Chip size="small" label={`ok ${f.counts.upToDate}`} sx={{ height: 20, fontSize: 11, bgcolor: '#4CAF5022', color: '#A5D6A7' }} />
          </Box>

          <Box sx={{ px: 1.5, pt: 1, display: 'flex', gap: 1 }}>
            <TextField
              select variant="standard" label="Status" value={f.statusFilter} fullWidth
              onChange={(e) => f.setStatusFilter(e.target.value)}
              sx={selectSx} InputLabelProps={{ sx: { color: `${c.text}99`, fontSize: 12 } }}
              SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: c.button } } } }}
            >
              {STATUS_FILTERS.map((s) => (
                <MenuItem key={s.key} value={s.key} sx={{ color: c.text, fontSize: 13 }}>{s.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select variant="standard" label="Location" value={f.locationFilter} fullWidth
              onChange={(e) => f.setLocationFilter(e.target.value)}
              sx={selectSx} InputLabelProps={{ sx: { color: `${c.text}99`, fontSize: 12 } }}
              SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: c.button } } } }}
            >
              <MenuItem value="" sx={{ color: c.text, fontSize: 13 }}>Every location</MenuItem>
              {f.locations.map((l) => (
                <MenuItem key={l.name} value={l.name} sx={{ color: c.text, fontSize: 13 }}>
                  {`${l.name} (${l.count})`}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* The .bin files actually in use, with how many devices are on each
              and how many of those are behind. Its own row on a phone — the
              filenames are long and would crush the two selects above. */}
          <Box sx={{ px: 1.5, pt: 1 }}>
            <TextField
              select variant="standard" label="Firmware file" value={f.binFilter} fullWidth
              onChange={(e) => f.setBinFilter(e.target.value)}
              sx={selectSx} InputLabelProps={{ sx: { color: `${c.text}99`, fontSize: 12 } }}
              SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: c.button } } } }}
            >
              <MenuItem value="" sx={{ color: c.text, fontSize: 13 }}>
                {`Every file (${f.binFiles.length})`}
              </MenuItem>
              {f.binFiles.map((b) => (
                <MenuItem key={b.name} value={b.name} sx={{ color: c.text, fontSize: 12 }}>
                  {`${b.name} · ${b.count}${b.needsUpdate ? ` · ${b.needsUpdate} stale` : ''}`}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ px: 1.5, pt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small" placeholder="Search…" value={f.search} fullWidth
              onChange={(e) => f.setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: `${c.text}99` }} /></InputAdornment>,
              }}
              inputProps={{ style: { color: c.text, fontSize: 13 } }}
            />
            <FormControlLabel
              sx={{ mr: 0, whiteSpace: 'nowrap' }}
              control={<Checkbox size="small" checked={f.liveOnly} onChange={(e) => f.setLiveOnly(e.target.checked)} />}
              label={<Typography sx={{ color: c.text, fontSize: 12 }}>Live</Typography>}
            />
            {/* Routes offline devices through the server's pending-command slot
                instead of MQTT — same option as the desktop page, same hook. */}
            <FormControlLabel
              sx={{ mr: 0, whiteSpace: 'nowrap' }}
              control={<Checkbox size="small" checked={f.queueOffline} onChange={(e) => f.setQueueOffline(e.target.checked)} />}
              label={<Typography sx={{ color: c.text, fontSize: 12 }}>Queue</Typography>}
            />
          </Box>

          <Box sx={{ px: 1.5, pt: 0.5, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Checkbox
              size="small"
              checked={f.allSelected}
              indeterminate={f.someSelected && !f.allSelected}
              disabled={f.selectable.length === 0}
              onChange={f.toggleAll}
              sx={{ color: c.text }}
            />
            {/* Counts for what is ON SCREEN — filtering to one .bin file and
                still reading the estate total answers a question nobody asked. */}
            <Typography sx={{ color: `${c.text}99`, fontSize: 12, flex: 1 }}>
              {`${f.visible.length} shown · ${f.visibleCounts.needsUpdate} need · ${f.targets.length} picked`}
            </Typography>
            <Button size="small" onClick={f.selectNeeding} sx={{ color: c.text, fontSize: 11, textTransform: 'none' }}>
              Select needing
            </Button>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.5, pt: 1, pb: 9 }}>
            {f.visible.length === 0 && (
              <Typography sx={{ color: `${c.text}99`, textAlign: 'center', py: 4, fontSize: 13 }}>
                Nothing matches these filters.
              </Typography>
            )}
            {f.visible.map((r) => {
              const offline = r.liveState !== FLEET_LIVE;
              const st = STATE_STYLE[r.state] || STATE_STYLE.unknown;
              return (
                <Box
                  key={r.uno}
                  onClick={offline ? undefined : () => f.toggle(r.uno)}
                  sx={{
                    bgcolor: c.button, borderRadius: '10px', p: 1.25, mb: 1,
                    display: 'flex', gap: 1, alignItems: 'flex-start',
                    opacity: offline ? 0.6 : 1,
                    cursor: offline ? 'default' : 'pointer',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={f.selected.has(r.uno)}
                    disabled={offline}
                    onChange={() => f.toggle(r.uno)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ p: 0.5, color: c.text }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography sx={{ color: c.text, fontWeight: 700, fontSize: 13, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.deviceName}
                      </Typography>
                      <Chip
                        size="small" label={st.label}
                        sx={{ height: 18, fontSize: 10, bgcolor: `${st.colour}33`, color: st.colour }}
                      />
                    </Box>
                    <Typography sx={{ color: `${c.text}99`, fontSize: 11, fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>
                      {r.uno}
                    </Typography>
                    <Typography sx={{ color: `${c.text}AA`, fontSize: 11 }}>
                      {`${r.locationName} · ${r.roomName}`}
                    </Typography>
                    <Typography sx={{ color: `${c.text}88`, fontSize: 10.5, mt: 0.25 }}>
                      {`OTA file ${when(r.builtAt)} · code ${when(r.flashedAt)}`}
                    </Typography>
                    <Typography sx={{ color: offline ? '#EF9A9A' : '#A5D6A7', fontSize: 10.5 }}>
                      {offline ? `offline · ${ago(r.age)}` : 'live'}
                      {r.binFile ? ` · ${r.binFile}` : ''}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Fixed above the list, because the selection is made by scrolling
              and a button at the bottom of 1,300 rows is a button nobody finds. */}
          {f.targets.length > 0 && f.phase !== 'sending' && (
            <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: 1.5, bgcolor: c.back }}>
              <Button
                fullWidth variant="contained" color="warning"
                startIcon={<SystemUpdateAltIcon />}
                onClick={() => setConfirmOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                {`Update ${f.targets.length} device${f.targets.length === 1 ? '' : 's'}`}
              </Button>
            </Box>
          )}
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} {...FIT_XS}>
        <DialogTitle>{`Update ${f.targets.length} device${f.targets.length === 1 ? '' : 's'}?`}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning">
            <Typography variant="caption" sx={{ display: 'block' }}>
              Command published to each selected device:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, wordBreak: 'break-all' }}>
              {f.payload}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              They download new firmware and reboot. This cannot be undone.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => { setConfirmOpen(false); f.run(); }}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Same dialog as the desktop page — publishing must not be able to
          differ between the two shells. */}
      <UploadBinFileDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <DeviceFirmwareDialog open={mapOpen} onClose={() => setMapOpen(false)} />
    </Box>
  );
}
